import { useEffect, useMemo, useRef } from "react";
import { Group, Mesh, MeshStandardMaterial, Vector3 as ThreeVector3 } from "three";
import { useFrame, Vector3 } from "@react-three/fiber";
import { animated, easings, useSpring } from "react-spring";
import { a } from "@react-spring/three";
import { Raycast } from "@utils/Raycast";
import { AAnnotation, AAnnotationProps } from "@components/Annotation";
import Packaging, { useHandle as usePackaging } from "@meshes/Packaging_3v0";
import GenericPackaging, { useHandle as useGenericPackaging } from "@meshes/GenericPackaging_2v1";
import Liner, { useHandle as useLiner } from "@meshes/Liner_3v0";
import Contents from "@meshes/Contents_3v0";
import Coolant from "@meshes/Coolant_3v0";
import { Graph, Grid, sampleCurve } from "@components/Graph";
import { MathUtils } from "@utils/MathUtils";
import { AppData, Spring } from "@App";
import { EScenario } from "@utils/Scenarios";
import { useViewportData } from "@components/ViewportManager";
import { Circle, Text } from "@react-three/drei";
import { CircularDesignMetric } from "./CircularDesignMetric";
import { Reflector } from "@components/Reflector";
import { DeviceProps } from "@components/Root";
import { assetPath } from "@utils/Config";
import { minTime } from "@utils/TimeMap";

const flapSpring: Spring = { tension: 100, friction: 30 };
const unboxSpring: Spring = { tension: 350, friction: 30, mass: 2 };
const reboxSpring: Spring = { tension: 250, friction: 30, mass: 1 };
const opacitySpring: Spring = { duration: 1000, easing: easings.easeOutQuad };
const landSpring: Spring = { tension: 500, friction: 200, mass: 5 };

// DS Smith Cardboard 6GP temperature data (sampled every 30 min from ISTA 7D Summer 48h test)
const dsData: [number, number][] = [[0.0167,1.2],[0.5167,-12.4],[1.0167,-12.4],[1.5167,-10.6],[2.0167,-8.9],[2.5167,-7.2],[3.0167,-5.8],[3.5167,-4.4],[4.0167,-3.3],[4.5167,-2.3],[5.0167,-1.5],[5.5167,-0.8],[6.0167,-0.4],[6.5167,-0.3],[7.0167,-0.2],[7.5167,-0.1],[8.0167,0],[8.5167,0],[9.0167,0],[9.5167,0.1],[10.0167,0.2],[10.5167,0.2],[11.0167,0.2],[11.5167,0.2],[12.0167,0.3],[12.5167,0.3],[13.0167,0.3],[13.5167,0.3],[14.0167,0.3],[14.5167,0.3],[15.0167,0.3],[15.5167,0.3],[16.0167,0.3],[16.5167,0.4],[17.0167,0.4],[17.5167,0.4],[18.0167,0.4],[18.5167,0.4],[19.0167,0.4],[19.5167,0.4],[20.0167,0.4],[20.5167,0.5],[21.0167,0.5],[21.5167,0.5],[22.0167,0.5],[22.5167,0.5],[23.0167,0.5],[23.5167,0.6],[24.0167,0.6],[24.5167,0.6],[25.0167,0.6],[25.5167,0.6],[26.0167,0.7],[26.5167,0.7],[27.0167,0.7],[27.5167,0.8],[28.0167,0.8],[28.5167,0.8],[29.0167,0.8],[29.5167,0.9],[30.0167,0.9],[30.5167,1],[31.0167,1],[31.5167,1],[32.0167,1.1],[32.5167,1.2],[33.0167,1.2],[33.5167,1.3],[34.0167,1.3],[34.5167,1.4],[35.0167,1.5],[35.5167,1.5],[36.0167,1.6],[36.5167,1.7],[37.0167,1.7],[37.5167,1.8],[38.0167,1.9],[38.5167,2],[39.0167,2.1],[39.5167,2.2],[40.0167,2.3],[40.5167,2.4],[41.0167,2.6],[41.5167,2.7],[42.0167,2.9],[42.5167,3.1],[43.0167,3.3],[43.5167,3.6],[44.0167,4],[44.5167,4.4],[45.0167,4.9],[45.5167,5.5],[46.0167,6],[46.5167,6.6],[47.0167,7.3],[47.5167,8],[48,8.6]];
// EPS 4GP temperature data (sampled every 30 min from ISTA 7D Summer 48h test)
const epsData: [number, number][] = [[0.0167,1.3],[0.5167,-8],[1.0167,-7.6],[1.5167,-5.7],[2.0167,-4.1],[2.5167,-2.4],[3.0167,-1],[3.5167,0.1],[4.0167,0.9],[4.5167,1.4],[5.0167,2.6],[5.5167,3.3],[6.0167,3.7],[6.5167,3.8],[7.0167,3.5],[7.5167,3.4],[8.0167,3.4],[8.5167,3.5],[9.0167,3.5],[9.5167,3.5],[10.0167,3.6],[10.5167,3.6],[11.0167,3.6],[11.5167,3.6],[12.0167,3.7],[12.5167,3.7],[13.0167,3.7],[13.5167,3.7],[14.0167,3.8],[14.5167,3.8],[15.0167,3.8],[15.5167,3.9],[16.0167,3.9],[16.5167,3.9],[17.0167,4],[17.5167,4],[18.0167,4.1],[18.5167,4.1],[19.0167,4.2],[19.5167,4.3],[20.0167,4.3],[20.5167,4.4],[21.0167,4.5],[21.5167,4.5],[22.0167,4.6],[22.5167,4.7],[23.0167,4.8],[23.5167,4.9],[24.0167,4.9],[24.5167,5],[25.0167,5],[25.5167,5.1],[26.0167,5.2],[26.5167,5.3],[27.0167,5.4],[27.5167,5.5],[28.0167,5.6],[28.5167,5.7],[29.0167,5.8],[29.5167,5.9],[30.0167,6],[30.5167,6],[31.0167,6.1],[31.5167,6.2],[32.0167,6.4],[32.5167,6.5],[33.0167,6.6],[33.5167,6.7],[34.0167,6.9],[34.5167,7],[35.0167,7.1],[35.5167,7.3],[36.0167,7.4],[36.5167,7.6],[37.0167,7.8],[37.5167,8],[38.0167,8.2],[38.5167,8.4],[39.0167,8.6],[39.5167,8.9],[40.0167,9.2],[40.5167,9.5],[41.0167,9.9],[41.5167,10.3],[42.0167,10.8],[42.5167,11.4],[43.0167,12.5],[43.5167,13.4],[44.0167,14.3],[44.5167,15.2],[45.0167,15.9],[45.5167,16.6],[46.0167,17.3],[46.5167,18],[47.0167,18.7],[47.5167,19.3],[48,19.8]];

function lookupTemp(data: [number, number][], time: number): number {
    if (time <= data[0][0]) return data[0][1];
    if (time >= data[data.length - 1][0]) return data[data.length - 1][1];
    let lo = 0, hi = data.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (data[mid][0] <= time) lo = mid; else hi = mid;
    }
    const t = (time - data[lo][0]) / (data[hi][0] - data[lo][0]);
    return data[lo][1] + t * (data[hi][1] - data[lo][1]);
}

const temperatureCurve = (time: number) => lookupTemp(dsData, time);
const genericTemperatureCurve = (time: number) => lookupTemp(epsData, time);
const epsColor = "#575656";
const dsColor = "#ff5700";

export function Models({ mobile, scale }: DeviceProps) {
    const { scenario, passedTime } = useViewportData<AppData>("main")!;

    const open = scenario === EScenario.Home2 || scenario === EScenario.Explode;
    const explode = scenario === EScenario.Explode;
    const temp = scenario === EScenario.Temperature;
    const metr = scenario === EScenario.Metrics;

    const [{ value: packagingTime }] = useSpring({ to: { value: open ? 1.0 : 0.0 }, delay: open ? 0 : 950, config: flapSpring }, [open]);
    const [{ value: linerTime }] = useSpring({ to: { value: open ? 1.0 : 0.0 }, delay: open ? 950 : 100, config: flapSpring }, [open]);

    const [{ value: linerY }] = useSpring({ to: { value: open ? 1.5 : 0.0 }, delay: open ? 500 : 550, config: open ? unboxSpring : reboxSpring }, [open]);
    const [{ value: coolantsY }] = useSpring({ to: { value: open ? 1.0 : 0.0 }, delay: open ? 1350 : 100, config: open ? unboxSpring : reboxSpring }, [open]);
    const [{ value: coolantsOffset }] = useSpring({ to: { value: open ? 0.1 : 0.0 }, delay: open ? 1450 : 0, config: open ? unboxSpring : reboxSpring }, [open]);
    const [{ value: contentsY }] = useSpring({ to: { value: open ? 1.6 : 0.0 }, delay: open ? 1350 : 100, config: open ? unboxSpring : reboxSpring }, [open]);

    const [{ value: offsetX }] = useSpring({ to: { value: metr ? -1.0 : 0.0 }, config: landSpring }, [metr]);
    const [{ value: offsetY }] = useSpring({ to: { value: temp ? mobile ? 0.6 : 0.3 : 0.0 }, config: landSpring }, [mobile, temp]);

    const [{ value: genericPackagingY }] = useSpring({ to: { value: temp || metr ? 0.0 : 2.0 }, delay: temp || metr ? 1000 : 0, config: landSpring }, [temp, metr]);

    const [{ value: genericPackagingOpacity }] = useSpring({ to: { value: temp || metr ? 1.0 : 0.0 }, delay: temp || metr ? 1000 : 0, config: landSpring }, [temp, metr]);

    const [{ value: explodeAnnotationOpacity }] = useSpring({ to: { value: explode ? 1.0 : 0.0 }, delay: explode ? 2000 : 0, config: opacitySpring }, [explode]);
    const [{ value: tempAnnotationOpacity }] = useSpring({ to: { value: temp ? 1.0 : 0.0 }, delay: temp ? 2000 : 0, config: opacitySpring }, [temp]);

    const [{ value: metricsOpacity }] = useSpring({ to: { value: metr ? 1.0 : 0.0 }, delay: metr ? 2000 : 0, config: opacitySpring }, [metr]);
    const [{ value: labelOpacity }] = useSpring({ to: { value: metr ? 0.0 : 1.0 }, config: opacitySpring }, [metr]);
    const [{ value: titleOpacity }] = useSpring({ to: { value: metr ? 1.0 : 0.0 }, config: opacitySpring }, [metr]);

    const temperature = temperatureCurve(passedTime);
    const genericTemperature = genericTemperatureCurve(passedTime);

    const annotation: AAnnotationProps = {
        size: 80,
        dotSize: 15
    }

    const explodeAnnotation: AAnnotationProps = {
        ...annotation,
        line: !mobile,
        relative: !mobile,
        opacity: explodeAnnotationOpacity,
        scale: 1.4,
        textPosition: mobile ? [80, 120] : [180, -30],
        textStyle: { width: mobile ? "calc(var(--page-width) - 160rem)" : "500rem", color: "var(--color-main)" },
        margin: mobile ? 0 : 20,
    };

    const tempAnnotation: AAnnotationProps = {
        ...annotation,
        line: true,
        relative: true,
        alwaysVisible: true,
        opacity: tempAnnotationOpacity,
        textStyle: { width: "130rem" }
    }

    return (
        <>
            <a.group position-x={offsetX} position-y={offsetY}>
                <APackaging time={packagingTime} labelOpacity={labelOpacity} titleOpacity={titleOpacity} />
                <AAnnotation {...explodeAnnotation} offset={0}
                    position={[0, 0.65, 0.75]}
                    title="Transport Packaging" body={<p>Tailored to customer's products dimensions and collation 100% recyclable, FSC Certified outer box.<br />Customisation and printing available on request.</p>}
                />
                <AAnnotation {...tempAnnotation}
                    position={[0, 0.5, 0.6]} textPosition={mobile ? [-60, 120] : [-420, -20]}
                    color={dsColor}
                    title={`${temperature.toFixed(1)}°C`}
                    anchor={mobile ? "top" : "right"}
                    titleStyle={{ color: dsColor, fontSize: "38rem" }}
                />
                <AMetrics type="tailortemp" position={[0, 0.6, 0.5]} opacity={metricsOpacity} scale={mobile ? scale * 0.6 : scale * 0.95} />

                <a.group position-x={-1} position-y={mobile ? 2 : 1.5} position-z={-5}>
                    <AGraph mobile={mobile} opacity={tempAnnotationOpacity} />
                </a.group>

                <a.group position-x={2} position-y={genericPackagingY}>
                    <AGenericPackaging opacity={genericPackagingOpacity} labelOpacity={labelOpacity} titleOpacity={titleOpacity} />
                    <AAnnotation {...tempAnnotation}
                        position={[0, 0.5, 0.6]} textPosition={mobile ? [-50, 120] : [220, -40]}
                        color={epsColor}
                        title={`${genericTemperature.toFixed(1)}°C`}
                        anchor={mobile ? "top" : "left"}
                        titleStyle={{ color: epsColor, fontSize: "38rem" }}
                    />
                    <AMetrics type="generic" position={[0, 0.6, 0.5]} opacity={metricsOpacity} scale={mobile ? scale * 0.6 : scale * 0.95} />
                </a.group>

                <a.group position-y={linerY}>
                    <ALiner time={linerTime} />
                    <AAnnotation {...explodeAnnotation} offset={0.2}
                        position={[0, 0.65, 0.65]}
                        title="Insulant" body={<p>100% corrugated insulation,  which dimensions and insulating thickness has been computed by the DS&nbsp;Smith parametric model to meet the specific requirements of the customer and their temperature-sensitive products.<br /><br />The Insulant can be shipped flat or fully assembled to optimize inbound and outbound logistics and further reduce CO<sub>2</sub> emissions.</p>}
                    />

                    <a.group position-y={coolantsY}>
                        <ACoolant angle={0} offset={coolantsOffset} />
                        <ACoolant angle={90} offset={coolantsOffset} />
                        <ACoolant angle={180} offset={coolantsOffset} />
                        <ACoolant angle={270} offset={coolantsOffset} />
                        <AAnnotation {...explodeAnnotation} offset={0.4}
                            position={[0, 0.55, 0.55]}
                            title="Coolants" body={<p>Using DS&nbsp;Smith parametric model, we accurately compute the required quantity of cooling agent needed to maintain the required temperature for the expected duration.</p>}
                        />
                    </a.group>

                    <a.group position-y={contentsY}>
                        <AContents />
                        <AAnnotation {...explodeAnnotation} offset={0.5}
                            position={[0, 0.5, 0.3]}
                            title="Customer Product" body={<p>Temperature-sensitive goods.</p>}
                        />
                    </a.group>
                </a.group>

                <Reflector mobile={mobile} />

            </a.group>
        </>
    )
}

const APackaging = animated(({ time, labelOpacity, titleOpacity }: { time: number, labelOpacity: number, titleOpacity: number }) => {
    const packaging = usePackaging(useRef<Group>(null));

    useFrame((state, delta) => {
        if (!packaging.current) return;
        const action = packaging.current.actions.KeyAction;
        if (!action) return;
        const clip = action.getClip();
        const mixer = action.getMixer();
        if (!action.isRunning()) {
            action.play();
            mixer.setTime(0);
        }
        mixer.setTime(MathUtils.clamp(time * clip.duration, 0, clip.duration - 0.01));
    });

    return (<>
        <Packaging ref={packaging} scale={0.05} receiveShadow castShadow raycast={Raycast.Disabled} />
        <Text
            color={"#575656"} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
            anchorX="center"
            fillOpacity={labelOpacity} material-depthWrite={labelOpacity > 0}
            position={[0, 0.08, 0.792]}
            scale={0.09}
        >
            TailorTemp®
        </Text>
        <Text
            color={"#ff7900"} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
            anchorX="center"
            fillOpacity={titleOpacity} material-depthWrite={titleOpacity > 0}
            position={[0, 1.45, 0.75]}
            scale={0.12}
        >
            TailorTemp®
        </Text>
    </>);
});

const AGenericPackaging = animated(({ opacity, labelOpacity, titleOpacity }: { opacity: number, labelOpacity: number, titleOpacity: number }) => {
    const genericPackaging = useGenericPackaging(useRef<Group>(null));

    useEffect(() => {
        if (!genericPackaging.current) return;
        if (!genericPackaging.current.root) return;
        genericPackaging.current.root.traverse((child) => {
            if (!(child instanceof Mesh)) return;
            if (!(child.material instanceof MeshStandardMaterial)) return;
            child.material.transparent = true;
            child.material.opacity = opacity;
            child.material.depthWrite = opacity > 0;
            child.material.depthTest = true;
        });
    }, [genericPackaging, opacity]);

    const actualLabelOpacity = labelOpacity * opacity;
    const actualTitleOpacity = titleOpacity * opacity;

    return (<>
        <GenericPackaging ref={genericPackaging} scale={0.05} receiveShadow castShadow raycast={Raycast.Disabled} />
        <Text
            color={"#575656"} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
            anchorX="center"
            fillOpacity={actualLabelOpacity} material-depthWrite={actualLabelOpacity > 0}
            position={[0, 0.12, 0.792]}
            scale={0.09}
        >
            Example EPS solution
        </Text>
        <Text
            color={"#575656"} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
            anchorX="center"
            fillOpacity={actualTitleOpacity} material-depthWrite={actualTitleOpacity > 0}
            position={[0, 1.45, 0.75]}
            scale={0.12}
        >
            Example EPS solution
        </Text>
    </>);
});

const ALiner = animated(({ time }: { time: number }) => {
    const liner = useLiner(useRef<Group>(null));

    useFrame((state, delta) => {
        if (!liner.current) return;
        const action = liner.current.actions.ThermalLinerRigAction;
        if (!action) return;
        const clip = action.getClip();
        const mixer = action.getMixer();
        if (!action.isRunning()) {
            action.play();
            mixer.setTime(0);
        }
        mixer.setTime(MathUtils.clamp(time * clip.duration, 0, clip.duration - 0.01));
    });

    return (
        <Liner ref={liner} scale={0.05} receiveShadow castShadow raycast={Raycast.Disabled} />
    );
});

const AContents = animated(() => {
    return (
        <Contents scale={0.05} receiveShadow castShadow raycast={Raycast.Disabled} />
    );
});

const ACoolant = animated(({ offset, angle }: { offset: number, angle: number }) => {
    const position = new ThreeVector3(11.0276928, 10.9015865, 8.6999998).multiplyScalar(0.05);
    if ((angle > 45 && angle < 135) || (angle > 225 && angle < 315)) {
        position.z = 0;
    } else {
        position.x = 0;
    }
    if (angle > 135 && angle < 315) {
        position.x *= -1;
        position.z *= -1;
    }
    const direction = position.clone();
    direction.y = 0;
    direction.normalize().multiplyScalar(offset);
    position.add(direction);
    position.x += 0.3276931 * 0.05;
    return (
        <Coolant scale={0.05} rotation-y={angle * MathUtils.deg2Rad} position={position} receiveShadow castShadow raycast={Raycast.Disabled} />
    );
});

const AGraph = animated(({ mobile, opacity }: { mobile: boolean, opacity: number }) => {
    const { passedTime } = useViewportData<AppData>("main")!;

    const minTemp = -13;
    const maxTemp = 23;
    const samples = Math.ceil(Math.max(passedTime, 1) * 4);

    const temperaturePoints = useMemo(() => {
        return sampleCurve(temperatureCurve, samples, 0, passedTime, 0.01);
    }, [samples, passedTime]);

    const genericTemperaturePoints = useMemo(() => {
        return sampleCurve(genericTemperatureCurve, samples, 0, passedTime);
    }, [samples, passedTime]);

    const currentTemperature = temperatureCurve(passedTime);
    const currentGenericTemperature = genericTemperatureCurve(passedTime);

    const xDisplayMax = 54;
    const width = mobile ? 4.5 : 10.125;
    const posX = mobile ? 1.25 : -1.0625;

    return (
        <group position-x={posX}>
            <Grid
                xLines={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50]}
                yLines={[-10, -5, 0, 5, 10, 15, 20]}
                xSpan={[0, 50]} ySpan={[-10, 20]}
                xRange={[minTime, xDisplayMax]} yRange={[minTemp, maxTemp]}
                xSize={width} ySize={1.95}
                color="#ffffff"
                opacity={opacity}
            />
            <Graph points={genericTemperaturePoints}
                xRange={[minTime, xDisplayMax]} yRange={[minTemp, maxTemp]}
                xSize={width} ySize={1.95}
                drawAxes={false}
                color={epsColor}
                thickness={2}
                opacity={opacity}
            />
            <Circle args={[0.05, 32]} position={[
                MathUtils.mapRange(passedTime, minTime, xDisplayMax, 0, width),
                MathUtils.mapRange(currentGenericTemperature, minTemp, maxTemp, 0, 1.95),
                0.009
            ]}
            >
                <meshBasicMaterial color={epsColor}
                    transparent={true} depthWrite={opacity > 0} opacity={opacity}
                />
            </Circle>
            <Graph points={temperaturePoints}
                xRange={[minTime, xDisplayMax]} yRange={[minTemp, maxTemp]}
                xSize={width} ySize={1.95}
                drawAxes={true} labels={["time (h)", "temperature (°C)"]} legend={[[5, 10, 15, 20, 25, 30, 35, 40, 45, 50], [-10, -5, 0, 5, 10, 15, 20]]}
                color={dsColor}
                thickness={3}
                axesColor={"#ffffff"}
                axesThickness={2}
                opacity={opacity}
            />
            <Circle args={[0.05, 32]} position={[
                MathUtils.mapRange(passedTime, minTime, xDisplayMax, 0, width),
                MathUtils.mapRange(currentTemperature, minTemp, maxTemp, 0, 1.95),
                0.019
            ]}
            >
                <meshBasicMaterial color={dsColor}
                    transparent={true} depthWrite={opacity > 0} opacity={opacity}
                />
            </Circle>
        </group>
    );
});

const badColor = "#ef7f1a";
const goodColor = "#7fa630";
const carbonColor = "#9d9e9e";

const AMetrics = animated(({ type, position, scale, opacity }: { type: "generic" | "tailortemp", position: Vector3, scale: number, opacity: number }) => {
    const generic = type === "generic";
    const optimized = generic ? 0.4 : 1.0;
    const recyclable = generic ? 0.0 : 1.0;
    const safe = generic ? 0.0 : 1.0;
    const utilisation = generic ? 1.0 : 0.995;
    const renewable = generic ? 0.0 : 1.0;
    const recycled = generic ? 0.0 : 1.0;
    const footprint = generic ? 2.1 : 1.2;
    const reuse = generic ? true : true;

    return (
        <>
            <CircularDesignMetric opacity={opacity} position={position} scale={scale} size={400} width={80} sections={[
                { label: "SUPPLY CHAIN OPTIMIZED", color: [badColor, goodColor], value: optimized, type: "percent", size: 1.3, labelStyle: { fontWeight: 700 } },
                { label: "RECYCLABLE", color: [badColor, goodColor], value: recyclable, type: "percent", size: 1 },
                { label: "PLANET SAFE", color: [badColor, goodColor], value: safe, type: "percent", size: 1 },
                { label: "MATERIAL UTILISATION", color: [badColor, goodColor], value: utilisation, type: "percent", size: 1 },
                { label: "RENEWABLE SOURCE", color: [badColor, goodColor], value: renewable, type: "percent", size: 1 },
                { label: "RECYCLED CONTENT", color: [badColor, goodColor], value: recycled, type: "percent", size: 1 },
                { label: "CARBON FOOTPRINT", color: [carbonColor, carbonColor], value: footprint, type: "unit", unit: "Kg", min: 0, max: 2.1, size: 1 },
                { label: "DESIGNED FOR REUSE", color: [badColor, goodColor], value: reuse, type: "bool", size: 1 },
            ]} />
        </>
    );
})